import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { PUBLIC_NAV_ITEMS, filterVisibleItems } from '../config/navigationConfig';
import { getZIndexClass } from '../config/zIndex';
import OroLockup from './OroLockup';

/**
 * PublicHeader Component
 * 
 * Reusable header for public pages using shared navigation config.
 * Uses md breakpoint for consistency with authenticated app navigation.
 * 
 * Related: ADR-0005 Navigation Shell & Menu Architecture
 */

interface PublicHeaderProps {
  showSignIn?: boolean;
}

export default function PublicHeader({ showSignIn = true }: PublicHeaderProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openLoginModal } = useAuthModal();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const visibleNavItems = filterVisibleItems(PUBLIC_NAV_ITEMS);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleSignIn = () => {
    closeMenu();
    
    // If already authenticated, redirect to app home
    if (isAuthenticated) {
      navigate('/home');
    } else {
      openLoginModal();
    }
  };

  return (
    <>
      <header className={`px-4 pt-6 pb-4 relative bg-oro-surface/95 backdrop-blur-sm border-b border-oro-border ${getZIndexClass('topBar')}`}>
        <div className="flex items-center justify-between">
          {/* #2544: the official horizontal lockup, not a hand-composed
              mark + text wordmark. Same brand-owner call as the web login
              modal (#2523) and the native login screen (#2516).

              `size` is the WIDTH here — the horizontal asset is trimmed to its
              ink, so height follows the 1200x289 aspect (~36px at 150).

              NOT `decorative`: with the text span gone, the lockup's
              alt="ORO move" is this button's only accessible name. */}
          <button
            onClick={() => navigate('/')}
            className="hover:opacity-80 transition-opacity"
          >
            <OroLockup orientation="horizontal" size={150} />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {visibleNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                {item.label}
              </Link>
            ))}
            {showSignIn && (
              <button
                onClick={handleSignIn}
                className="text-sm text-accent hover:text-accent-light transition-colors font-medium ml-4"
              >
                Sign in
              </button>
            )}
          </div>

          {/* Mobile Hamburger Menu */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="p-2 text-text-primary hover:text-accent transition-colors"
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu - rendered via portal to escape stacking context issues (#1331) */}
      {isMenuOpen && createPortal(
        <div className={`fixed top-16 right-4 w-64 bg-oro-surface border border-oro-border rounded-lg shadow-lg ${getZIndexClass('overlay')} md:hidden`}>
          <nav className="py-2" role="menu">
            {visibleNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMenu}
                className="block px-4 py-3 text-text-primary hover:bg-oro-surface-alt transition-colors"
                role="menuitem"
              >
                {item.label}
              </Link>
            ))}
            {showSignIn && (
              <>
                <hr className="border-oro-border my-2" />
                <button
                  onClick={handleSignIn}
                  className="block w-full text-left px-4 py-3 text-accent hover:bg-oro-surface-alt transition-colors font-medium"
                  role="menuitem"
                >
                  Sign in
                </button>
              </>
            )}
          </nav>
        </div>,
        document.body
      )}
    </>
  );
}